import React from 'react';
import yml from 'js-yaml';
import { graphql, useStaticQuery } from 'gatsby';
import { GatsbyImage } from 'gatsby-plugin-image';

import cliImage from './images/SoftwareCLI.svg';
import ideImage from './images/SoftwareIDE.svg';
import ide2Image from './../product/images/SoftwareIDEv2.svg';
import iotImage from './images/SoftwareIoT.svg';
import linuxImage from './images/SoftwareLinux.svg';
import openMVImage from './images/OpenMV.svg';
import webEditorImage from './images/SoftwareWebEditor.svg';
import plcImage from './images/SoftwarePLC.svg';

import './ProductCompatibility.scss';

interface ProductCompatibilityProps {
  compatibility: string;
}

export default function ProductCompatibility({ compatibility }: ProductCompatibilityProps): React.ReactElement {
  const compatibilityObject = yml.load(compatibility) || {
    software: [],
    hardware: {
      boards: [],
      shields: [],
      carriers: [],
    },
  };

  return (
    <div className="compatibility">
      {compatibilityObject.software && compatibilityObject.software.length && (
        <div className="compatibility__box">
          <h3>Software</h3>
          <p>The following software tools allow you to program your board both online and offline.</p>
          <div className="compatibility__list">
            <SoftwareList software={compatibilityObject.software} />
          </div>
        </div>
      )}
      {compatibilityObject.hardware && (
        <div className="compatibility__box">
          <h3>Hardware</h3>
          <p>The hardware listed below is compatible with this product.</p>
          <HardwareList hardware={compatibilityObject.hardware} />
        </div>
      )}
    </div>
  );
}

function SoftwareList({ software = [] }) {
  const SOFTWARE_IMAGES = {
    'arduino-ide': {
      image: ideImage,
      text: 'Arduino IDE',
      url: 'https://www.arduino.cc/en/software',
      external: true,
    },
    'arduino-ide-v2': {
      image: ide2Image,
      text: 'Arduino IDE 2',
      url: '../../../software/ide-v2',
      external: false,
    },
    'arduino-cli': {
      image: cliImage,
      text: 'Arduino CLI',
      url: 'https://arduino.github.io/arduino-cli/',
      external: true,
    },
    'iot-cloud': {
      image: iotImage,
      text: 'IoT Cloud',
      url: 'https://create.arduino.cc/iot',
      external: true,
    },
    'web-editor': {
      image: webEditorImage,
      text: 'Web Editor',
      url: 'https://create.arduino.cc/editor',
      external: true,
    },
    'manager-linux': {
      image: linuxImage,
      text: 'Manager for Linux',
      url: 'https://create.arduino.cc/devices/',
      external: true,
    },
    'openmv-ide': {
      image: openMVImage,
      text: 'OpenMV IDE',
      url: 'https://openmv.io/pages/download',
      external: true,
    },
    'plc-ide': {
      image: plcImage,
      text: 'Arduino PLC IDE',
      url: 'https://www.arduino.cc/pro/software-plc-ide',
      external: true,
    },
  };
  return software.map((item) => {
    return (
      SOFTWARE_IMAGES[item] && (
        <a
          key={item}
          className="compatibility__software"
          href={SOFTWARE_IMAGES[item].url}
          target={SOFTWARE_IMAGES[item].external ? '_blank' : ''}
          rel="noreferrer"
        >
          <img src={SOFTWARE_IMAGES[item].image} alt={SOFTWARE_IMAGES[item].text} />
          <span>{SOFTWARE_IMAGES[item].text}</span>
        </a>
      )
    );
  });
}

function HardwareList({ hardware = [] }) {
  const data = useStaticQuery(graphql`
    query {
      images: allFile(filter: { base: {regex: "/\\image.(jpe?g|png|gif|bmp|svg)$/i"} }) {
        edges {
          node {
            childImageSharp {
              gatsbyImageData(layout: FIXED, width: 102, quality: 90, placeholder: BLURRED)
            }
            publicURL
            extension
            relativeDirectory
          }
        }
      }
      products: allFile(filter: { fields: { type: { eq: "product" } } }) {
        edges {
          node {
            childMdx {
              frontmatter {
                title
              }
            }
            relativeDirectory
          }
        }
      }
    }
  `);

  const images = {};
  data.images.edges.forEach((element) => {
    const parts = element.node.relativeDirectory.split('/');
    const key = parts[parts.length - 1];
    images[key] = element.node;
  });

  const products = {};
  data.products.edges.forEach((element) => {
    const parts = element.node.relativeDirectory.split('/');
    const key = parts[parts.length - 1];
    products[key] = element.node;
  });

  return Object.keys(hardware).map((section) => {
    if (hardware[section] && hardware[section].length) {
      return (
        <div className="compatibility__hardware" key={section}>
          <h2>{section}</h2>
          <div className="compatibility__list">
            {hardware[section].map((item) => {
              if (!images[item] || !products[item]) {
                return null;
              }
              return (
                <div className="compatibility__hardware--item" key={item}>
                  <a className="compatibility__link" href={'/hardware/' + item}>
                    {images[item] && (
                      <div className="compatibility__image">
                        {images[item].extension === 'svg' ? (
                          <img src={images[item].publicURL} alt={`${item}`} />
                        ) : (
                          <GatsbyImage
                            alt={`${item}`}
                            objectFit="contain"
                            style={{ height: '100%' }}
                            imgStyle={{ objectFit: 'contain' }}
                            image={images[item].childImageSharp.gatsbyImageData}
                          />
                        )}
                      </div>
                    )}
                    {products[item] && <div className="compatibility__title">{products[item].childMdx.frontmatter.title}</div>}
                  </a>
                </div>
              );
            })}
          </div>
        </div>
      );
    }
    return null;
  });
}
